import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import * as impactedAssetsActions from './impacted-assets.actions';
import produce from 'immer';

import { ImpactedAssetsState } from './impacted-assets.model';
import { Layer } from '../cop/models';
import { DocumentQueryPagination, FilterGroup, SchemaField } from 'src/app/modules/core/store/models';

export const impactedAssetsModuleKey = 'impactedAssets';
export const impactedAssetsFeatureKey = 'impactedAssets';

export const initialImpactedAssetsState: ImpactedAssetsState = {
  emailSummaries: [],
  assetEmailsExist: false,
  selectedDocumentAssetsByDistance: [],
  selectedShapesAndDocumentsOnMap: null,
  selectedDocumentClosestCounts: {
    countsByLayer: [],
    isControlShown: false
  },
  communicationMeta: {
    isDrawn: false,
    metersWithin: 0
  },
  closestAssetEmailMeta: {
    selectionFilterDto: null
  },
  closestAssetRequest: {
    assetRequest: null,
    forEvent: 'selection'
  },
  closestAssetDropdownValue: null,
  ui: {
    isEmailWorking: false,
    isClosestRSDTakeover: false,
    isClosestCountWorking: false,
    isClosestWorking: false
  }
};

export const reducer: ActionReducer<ImpactedAssetsState, Action> = createReducer(
  initialImpactedAssetsState,
  on(impactedAssetsActions.setClosestAssetRequest, (state, action) => {
    return {
      ...state,
      closestAssetRequest: {
        assetRequest: action.closestAssetDto,
        forEvent: action.forEvent
      }
    };
  }),
  on(impactedAssetsActions.addSelectedDocumentAssetsSummary, (state, action) => {
    const newLayerUIds = action.assetsByDistance?.map(a => a.layerUId);
    const filteredList = state.selectedDocumentAssetsByDistance?.filter(d => !newLayerUIds?.includes(d.layerUId));
    const newList = filteredList?.concat(action.assetsByDistance);

    return {
      ...state,
      selectedDocumentAssetsByDistance: newList
    };
  }),
  on(impactedAssetsActions.clearSelectedDocumentAssetsSummary, (state, action) => {
    return {
      ...state,
      selectedDocumentAssetsByDistance: []
    };
  }),
  on(impactedAssetsActions.setClosestAssetCounts, (state, action) => {
    return {
      ...state,
      selectedDocumentClosestCounts: {
        countsByLayer: action.countsByLayer,
        isControlShown: action.isControlShown
      }
    };
  }),
  on(impactedAssetsActions.setClosestAssetDropdownValue, (state, action) => {
    return {
      ...state,
      closestAssetDropdownValue: action.val
    };
  }),
  on(impactedAssetsActions.setClosestIsWorking, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isClosestWorking: action.isWorking
      }
    };
  }),
  on(impactedAssetsActions.setClosestCountIsWorking, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isClosestCountWorking: action.isWorking
      }
    };
  }),
  on(impactedAssetsActions.setAssetEmailIsWorking, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isEmailWorking: action.isWorking
      }
    };
  }),
  on(impactedAssetsActions.setAssetEmailsExist, (state, action) => {
    return {
      ...state,
      assetEmailsExist: action.doEmailsExist
    };
  }),
  on(impactedAssetsActions.setAssetEmailSummaryComplete, (state, action) => {
    return {
      ...state,
      emailSummaries: action.emailSummaries
    };
  }),
  on(impactedAssetsActions.showHideAssetsRSDView, (state, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isClosestRSDTakeover: action.show
      }
    };
  }),
  on(impactedAssetsActions.setSelectionEmailMeta, (state, action) => {
    return {
      ...state,
      emailSummaries: [],
      closestAssetEmailMeta: {
        selectionFilterDto: action.selectionFilterDto
      }
    };
  }),
  on(impactedAssetsActions.setCommunicationMeta, (state, action) => {
    return {
      ...state,
      communicationMeta: {
        isDrawn: action.isDrawn,
        metersWithin: action.metersWithin
      }
    };
  }),
  on(impactedAssetsActions.setSelectedDocumentAssetsByDistance, (state, { selectedDocumentAssetsByDistance }) => ({
    ...state,
    selectedDocumentAssetsByDistance
  }))
);

export const getEmailSummariesForDocument = (state: ImpactedAssetsState) => state?.emailSummaries;

export const getEmailsExist = (state: ImpactedAssetsState) => state?.assetEmailsExist;

export const getIsEmailWorking = (state: ImpactedAssetsState) => state?.ui?.isEmailWorking;

export const getSelectedDocumentAssetsByDistance = (state: ImpactedAssetsState) => state?.selectedDocumentAssetsByDistance;

export const getClosestAssetRequest = (state: ImpactedAssetsState) => state?.closestAssetRequest;

export const getEmailMeta = (state: ImpactedAssetsState) => state?.closestAssetEmailMeta;

export const getSelectedDocumentClosestCounts = (state: ImpactedAssetsState) => state?.selectedDocumentClosestCounts;

export const getIsClosestRSDTakeover = (state: ImpactedAssetsState) => state?.ui?.isClosestRSDTakeover;

export const getCommunicationMeta = (state: ImpactedAssetsState) => state?.communicationMeta;

export const getClosestAssetDropdownValue = (state: ImpactedAssetsState) => state?.closestAssetDropdownValue;

export const getIsClosestAssetCountWorking = (state: ImpactedAssetsState) => state?.ui?.isClosestCountWorking;

export const getIsClosestAssetWorking = (state: ImpactedAssetsState) => state?.ui?.isClosestWorking;

export const getSelectedShapesAndDocumentsOnMap = (state: ImpactedAssetsState) => state?.selectedShapesAndDocumentsOnMap;
